import { CASH_LABELS } from './cash-monitor-model.js';

export function cashMonitorEventPostureView(event = {}) {
  const isLedgerTiming =
    event.provenanceKind === 'ledger-commercial-timing' ||
    event.postingPosture === 'PRE_POSTING_COMMERCIAL_TIMING';

  if (isLedgerTiming) {
    return {
      chipKind: 'ledger-commercial-timing',
      sourceLabel: event.sourceLabel || 'Ledger commercial timing',
      postureLabel: 'pre-posting timing',
      truthLabel: event.accountingTruth === true ? 'accounting truth' : 'not actual cash',
      tooltip: 'Ledger PO/SO commercial timing informs cash scenarios before a posted accounting fact exists. It is not live provider proof.',
    };
  }

  return {
    chipKind: event.provenanceKind || 'local',
    sourceLabel: event.sourceLabel || event.source || 'Scenario input',
    postureLabel: event.type === 'actual-cash' ? 'actual cash' : 'scenario timing',
    truthLabel: event.type === 'actual-cash' ? 'actual cash' : 'not actual cash',
    tooltip: 'Cash Monitor row posture is shown separately from posted accounting truth and live provider proof.',
  };
}

export function ledgerTimingSummaryView(events = []) {
  const count = events.length;
  return {
    count,
    chipKind: 'ledger-commercial-timing',
    label: `Ledger timing ${count}`,
    posture: 'Assumption / not actual cash',
    tooltip: 'Ledger PO/SO commercial timing is included as assumption-labeled cash timing, not posted actual cash, not live provider proof.',
  };
}

function hasLabel(item, label) {
  return Array.isArray(item?.labels) && item.labels.includes(label);
}

function isLedgerCommercialTiming(event) {
  return Boolean(
    event?.provenanceKind === 'ledger-commercial-timing' ||
    event?.postingPosture === 'PRE_POSTING_COMMERCIAL_TIMING'
  );
}

export function cashMonitorEvidenceSummaryView({
  openingCash,
  events = [],
  labelIssues = [],
} = {}) {
  const activeEvents = Array.isArray(events) ? events : [];
  const openingActual = hasLabel(openingCash, CASH_LABELS.ACTUAL_CASH) ? 1 : 0;
  const counts = {
    actualCashSources: openingActual + activeEvents.filter((event) => event.type === 'actual-cash').length,
    expectedReceipts: activeEvents.filter((event) => hasLabel(event, CASH_LABELS.EXPECTED_RECEIPTS)).length,
    committedObligations: activeEvents.filter((event) => hasLabel(event, CASH_LABELS.COMMITTED_OBLIGATIONS)).length,
    optionalPayments: activeEvents.filter((event) => hasLabel(event, CASH_LABELS.OPTIONAL_PAYMENTS)).length,
    assumptions: activeEvents.filter((event) => hasLabel(event, CASH_LABELS.ASSUMPTION)).length,
    scenarios: activeEvents.filter((event) => hasLabel(event, CASH_LABELS.SCENARIO)).length,
    sourceLocal: openingActual && hasLabel(openingCash, CASH_LABELS.SOURCE_LOCAL_DEMO)
      ? 1
      : 0,
    ledgerCommercialTiming: activeEvents.filter(isLedgerCommercialTiming).length,
    labelIssueCount: Array.isArray(labelIssues) ? labelIssues.length : 0,
  };
  counts.sourceLocal += activeEvents.filter((event) => hasLabel(event, CASH_LABELS.SOURCE_LOCAL_DEMO)).length;

  const status = counts.labelIssueCount > 0 ? 'label hold' : 'labels strict';
  const label = [
    `Actual evidence ${counts.actualCashSources}`,
    `Receipts ${counts.expectedReceipts}`,
    `Commitments ${counts.committedObligations}`,
    `Optional ${counts.optionalPayments}`,
    `Ledger timing ${counts.ledgerCommercialTiming}`,
  ].join(' · ');

  return {
    ...counts,
    chipKind: counts.labelIssueCount > 0 ? 'missing-warning' : 'computed',
    status,
    label,
    tooltip: [
      `Actual cash sources: ${counts.actualCashSources}.`,
      `Expected receipts: ${counts.expectedReceipts}.`,
      `Committed obligations: ${counts.committedObligations}.`,
      `Optional payments: ${counts.optionalPayments}.`,
      `Assumption-labeled events: ${counts.assumptions}.`,
      `Scenario-labeled events: ${counts.scenarios}.`,
      `Ledger PO/SO commercial timing events: ${counts.ledgerCommercialTiming}.`,
      `Source-local/demo labels: ${counts.sourceLocal}.`,
      counts.labelIssueCount > 0
        ? `Cash Monitor label validation issues: ${counts.labelIssueCount}.`
        : 'Cash Monitor label validation passed.',
      'This summary is cash-judgment evidence, not a posted-accounting-truth claim and not live bank/provider proof.',
    ].join(' '),
  };
}
